import { action, observable, computed } from 'mobx';
import { ValidationState, validateFields } from '../../../WebGiving/validation/view-model-validator';
import { PayerOnboardingPage } from '../index';

import {
	PayerOnboardingMerchantInfo,
	AddPaymentMethodResultCode,
	PersonCountryInfo,
	Country,
	PaymentMethodType,
	MerchantCardPreference,
} from '../../payer-onboarding-generated';
import { CardPaymentMethodViewModel } from './card-payment-method-view-model';
import { ITabOption } from '../../../WebGiving/components/tabs/tabs';
import { AchPaymentMethodViewModel } from './ach-payment-method-view-model';
import cardIcon from '../../../WebGiving/assets/svg/icon-card.svg';
import achIcon from '../../../WebGiving/assets/svg/checkbook.svg';
import { PayerOnboardingPageViewModelBase } from '../payer-onboarding-page-view-model';

import { PayerOnboardingErrorType, PayerOnboardingError } from '../../components/error-message/payer-onboarding-error';

export class PagePaymentMethodViewModel extends PayerOnboardingPageViewModelBase {
	readonly type: PayerOnboardingPage = PayerOnboardingPage.PaymentMethod;

	// mobx observable decorator removed as it caused issues when running our front end test suites
	// it has been replaced with an observable syntax which achieves similar behavior
	paymentMethods: ITabOption[] = observable([]);

	@computed
	get paymentMethodViewModel() : AchPaymentMethodViewModel | CardPaymentMethodViewModel {
		return this.paymentMethodType === PaymentMethodType.CreditCard ? this.cardModel : this.achModel;
	}

	get isAchSupported(): boolean {
		if (this.merchant.IsDefaultMerchant) {
			return this.personCountryInfo.CountryCode === Country.US;
		} else {
			return this.merchant.SupportsAch;
		}
	}

	get merchantSupportsCard() : boolean {
		return this.merchant.SupportedCardBrandKeys && this.merchant.SupportedCardBrandKeys.length > 0;
	}

	@observable
	paymentMethodType: PaymentMethodType;

	@observable
	validationState?: ValidationState;

	@observable
	cardModel: CardPaymentMethodViewModel;

	@observable
	achModel: AchPaymentMethodViewModel;

	private errorsByTab: { [key:number]: PayerOnboardingError};

	@action
	setError(error: PayerOnboardingError): void {

		if ( error
			&& error.errorType === PayerOnboardingErrorType.AddPaymentMethodError
			&& error.errorResultCode === AddPaymentMethodResultCode.CardTokenizationUnavailable
		) {
			this.createPaymentMethodTabs({cardTokenizationUnavailable: true});
		}

		this.error = error;
	}

	@action
	validate(): ValidationState {
		return validateFields(this.paymentMethodViewModel, 'Please enter valid details');
	}

	constructor(
		public merchant: PayerOnboardingMerchantInfo,
		private personCountryInfo: PersonCountryInfo,
		paymentMethodType?: PaymentMethodType,
		achModel?: AchPaymentMethodViewModel,
	) {
		super();
		this.achModel = achModel || new AchPaymentMethodViewModel();
		this.cardModel = new CardPaymentMethodViewModel(merchant, personCountryInfo.PostalCodeCommonName);
		this.paymentMethodType = paymentMethodType || merchant.PaymentMethodPreference;

		this.errorsByTab = {};
		this.createPaymentMethodTabs();

		// ensure there's a payment method selected
		if (!this.paymentMethodType) {
			this.paymentMethodType = this.paymentMethods[0].value;
		}
	}

	@action.bound
	switchPaymentMethodType (value: number) {
		if (this.paymentMethodType !== value) {
			this.validationState = undefined;
			this.paymentMethodViewModel.clearValidation();
		}

		if(this.error) {
			this.errorsByTab[this.paymentMethodType] = this.error;
		}
		this.error = this.errorsByTab[value];

		this.paymentMethodType = value;
	}

	createPaymentMethodTabs (options?: {cardTokenizationUnavailable: boolean}) {
		this.paymentMethods = [];
		const cardTabAvailabilityLabel = options && options.cardTokenizationUnavailable
			? ' (Not available)'
			: '';

		const cardTabLabel = this.merchant.CardPreference === MerchantCardPreference.AnyCard
			? 'Card' + cardTabAvailabilityLabel
			: 'Debit card' + cardTabAvailabilityLabel;

		if (this.merchantSupportsCard) {
			this.paymentMethods.push({
				label: cardTabLabel,
				value: PaymentMethodType.CreditCard,
				icon: cardIcon,
				acceptanceTestTargetId: 'tab-payment-method-card',
			});
		}

		if (this.isAchSupported) {
			this.paymentMethods.push({
				label: 'Bank account',
				value: PaymentMethodType.ACH,
				icon: achIcon,
				acceptanceTestTargetId: 'tab-payment-method-ach',
			});
		}
	}
}

